import React, { useState, useEffect, ChangeEvent, MouseEvent, KeyboardEvent } from 'react';
import forge from 'node-forge';
import { useRouter } from 'next/navigation';
import CryptoJS from 'crypto-js';
import { Checkbox, CheckboxChangeEvent } from 'antd';
import InputField from '@/components/Atoms/inputField';
import PrimaryButton from '@/components/Atoms/primaryButton';
import serviceUtil from '@/Utils/serviceUtil/serviceUtil';
import { getErrorMessage } from '@/Utils/messageUtils';
import CustomModal from '@/components/Atoms/CustomModal';
import Cookies from 'js-cookie';
import { logout } from '@/Utils/logoutUtil';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { getDecryptedPassword } from 'src/Utils/getDecryptedPassword/getDecryptedPassword';
import { encryptPasswordBeforeSending } from 'src/Utils/encryptPasswordWithRSA/encryptPasswordWithRSA';
import { appendCustomMenuItems } from 'src/Utils/menuUtil';

interface LoginBoxProps {
  apiUrl: string;
}

const LoginBox: React.FC<LoginBoxProps> = ({ apiUrl }) => {
  const router = useRouter();
  const { t } = useTranslation();

  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [description, setDescription] = useState({
    message: '',
    type: '',
    heading: '',
  });
  const [usernameError, setUsernameError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [ButtonTitle, setButtonTitle] = useState('');
  const [iconUrl, setIconUrl] = useState('');
  const [activeSession, setActiveSession] = useState<boolean>(false);

  useEffect(() => {
    const userInActivityTimeout = localStorage.getItem('USER_INACTIVITY_TIMEOUT');
    if (userInActivityTimeout === 'true') {
      setDescription(getErrorMessage('USER_INACTIVITY_TIMEOUT'));
      setIconUrl('/error.svg');
      setButtonTitle(t('OK'));
      setIsModalVisible(true);
      localStorage.removeItem('USER_INACTIVITY_TIMEOUT');
    }

    const fetchRememberMe = async () => {
      try {
        const response = await fetch('/api/auth/getRememberMe', {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          if (data.rememberMeFlag === 'true') {
            setUsername(data.username || '');
            setRememberMe(data.username !== '');

            const decryptedPassword = await getDecryptedPassword();
            setPassword(decryptedPassword);
          }
        }
      } catch (error) {
        console.error('Error fetching remember me');
      }
    };

    fetchRememberMe();
  }, []);

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    if (emailValue && !validateEmail(emailValue)) {
      setUsernameError('Email format is invalid.');
    } else {
      setUsernameError('');
    }
  };

  const handleLogout = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setActiveSession(false);
    logout(true);
  };

  const handleLoginClick = async (event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    let isValid = true;

    const trimmedUsername = username.trim();

    if (!trimmedUsername) {
      setUsernameError(t('Please enter your email id.'));
      isValid = false;
    } else if (!validateEmail(trimmedUsername)) {
      isValid = false;
    } else {
      setUsernameError('');
    }

    if (!password) {
      setPasswordError(t('Please enter a password.'));
      isValid = false;
    } else {
      setPasswordError('');
    }

    const publicKey = Buffer.from(process.env.NEXT_PUBLIC_RSA_PUBLIC_KEY ?? '', 'base64').toString('utf-8');
    const { encryptPassword, encryptedAESKey, iv, authTag } = await encryptPasswordBeforeSending(password, publicKey);

    if (isValid) {
      setLoading(true);

      try {
        const rememberMeResponse = await fetch('/api/auth/rememberMe', {
          method: 'POST',
          body: JSON.stringify({ username: trimmedUsername, encryptPassword, rememberMe, encryptedAESKey, iv, authTag }),
          headers: { 'Content-Type': 'application/json' },
        });

        const rememberMeData = await rememberMeResponse.json();
        if (!rememberMeResponse.ok) {
          throw new Error(rememberMeData.message);
        }

        const encryptedPasswordResponse = await fetch('/api/auth/getRememberMe');
        const encryptedPasswordData = await encryptedPasswordResponse.json();

        if (!encryptedPasswordData.encryptedPassword) {
          throw new Error('Failed to retrieve encrypted password.');
        }

        const encryptedPassword = encryptedPasswordData.encryptedPassword;
        const deviceTokenKey = `deviceToken_${trimmedUsername.toLowerCase()}`;
        const deviceToken = Cookies.get(deviceTokenKey) || localStorage.getItem(deviceTokenKey);
        const response = await serviceUtil.post(
          apiUrl,
          { email: trimmedUsername, password: encryptedPassword },
          deviceToken ? { 'x-device-token': deviceToken } : {},
          true,
        );

        if (response?.status === 200 || response?.status === 201) {
          // 2FA required -> store partial token and redirect to MFA page
          if (response.data?.data?.mfaRequired) {
            const partialToken = response.data?.data?.partialToken;
            if (partialToken) {
              localStorage.setItem('accessToken', partialToken);
              Cookies.set('accessToken', partialToken, {
                expires: 1 / 144, // 10 minutes
                secure: true,
                sameSite: 'Strict',
              });
            }
            // Persist the email this OTP challenge belongs to, so the MFA
            // screen can key the trusted-device token per user instead of
            // overwriting a single shared token across different accounts.
            const mfaEmail = response.data?.data?.email || username;
            localStorage.setItem('mfaLoginEmail', mfaEmail.toLowerCase());
            setLoading(false);
            router.push('/auth/two-step-verification');
            return;
          }

          localStorage.setItem('loginTime', new Date().getTime().toString());
          localStorage.setItem('accessToken', response.data?.data?.accessToken);
          Cookies.set('accessToken', response.data?.data?.accessToken, {
            expires: 1,
            secure: true,
            sameSite: 'Strict',
          });

          setLoading(false);
          localStorage.setItem('email', response.data.data?.email);
          localStorage.setItem('firstName', response.data.data?.firstName);
          localStorage.setItem('lastName', response.data.data?.lastName);
          checkSession(response.data?.data?.accessToken);
        } else {
          setLoading(false);
          setIsModalVisible(true);
          if (response.error.response.data.message === 'LOGIN_ACTIVE_TOKEN_FOUND_LOGOUT_TO_CONTINUE') {
            setIconUrl('/error.svg');
            setButtonTitle(t('Logout All'));
            setActiveSession(true);
            localStorage.setItem('accessToken', response.error.response.data.token);
            Cookies.set('accessToken', response.error.response.data.token, {
              expires: 1,
              secure: true,
              sameSite: 'Strict',
            });
            setDescription(getErrorMessage(response.error.response.data.message));
          } else {
            setIconUrl('/error.svg');
            setDescription(getErrorMessage(response.error.response.data.message));
            setButtonTitle(t('OK'));
            setActiveSession(false);
          }
          console.error('Login request failed', response?.error?.response?.status ?? 'unknown');
        }
      } catch (error) {
        setLoading(false);
        console.error('Login flow failed');
      }
    }
  };

  const checkSessionTimeout = () => {
    const SessionTimeoutMinutes = 600;
    const SessionTimeoutMilliseconds = SessionTimeoutMinutes * 60 * 1000;
    const loginTime = localStorage.getItem('loginTime');
    if (loginTime) {
      const currentTime = new Date().getTime();
      const elapsedTime = currentTime - parseInt(loginTime, 10);

      if (elapsedTime >= SessionTimeoutMilliseconds) {
        logout();
      }
    }
  };

  const checkSession = async (accessToken: string) => {
    const interval = setInterval(() => {
      checkSessionTimeout();
    }, 60000);

    if (!accessToken) {
      logout();
    } else {
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        lang: localStorage.getItem('languageSelected') ? localStorage.getItem('languageSelected') : 'en',
      };

      try {
        const response = await serviceUtil.get(`${process.env.NEXT_PUBLIC_API_URL}/identity/user/menu`, {}, headers);

        if (response?.status === 200) {
          const menuData = response?.data?.userData || response?.data?.data?.userData;
          const resolvedMenu = appendCustomMenuItems(Array.isArray(menuData) ? menuData : []);
          localStorage.setItem('userMenu', JSON.stringify(resolvedMenu));
          router.push('/home');
        } else {
          setDescription(getErrorMessage(response.error.response.data.message));
        }
      } catch (error: any) {
        if (error?.response?.data?.statusCode === 401) {
        }
      }
    }

    return () => clearInterval(interval);
  };

  const closeModal = () => {
    setIsModalVisible(false);
  };

  const handleRegisterClick = () => {
    router.push('/auth/registration');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleLoginClick(e);
    }
  };

  const handleUsernameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement | null;
    if (target && typeof target.setCustomValidity === 'function') {
      target.setCustomValidity('');
    }
    setUsername(e.target.value);
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleRememberMeChange = (e: CheckboxChangeEvent) => {
    setRememberMe(e.target.checked);
  };

  return (
    <div>
      <div className="mb-4 md:mb-2 lg:mb-3 mt-7">
        <InputField
          name="email"
          testId="login-email-input"
          inputType="email"
          value={username}
          onChange={handleUsernameChange}
          onInvalid={(e) => {
            const target = e.target as HTMLInputElement | null;
            if (target && typeof target.setCustomValidity === 'function') {
              target.setCustomValidity(t('Email-ID format is invalid.'));
            }
          }}
          placeholder={t('Username')}
          prefixIcon={'/mail.svg'}
          title={t('Email')}
          infoText={t('Enter your email')}
          validate
          onBlur={handleEmailBlur}
          onKeyDown={handleKeyDown}
          errorMessage={usernameError}
          heightProp={'6vmin'}
        />
      </div>
      <div className="mb-4 md:mb-2 lg:mb-3">
        <InputField
          name="password"
          testId="login-password-input"
          inputType="password"
          value={password}
          onChange={handlePasswordChange}
          placeholder={t('Password')}
          prefixIcon={'/password.svg'}
          title={t('Password')}
          infoText={t('Enter your password')}
          onKeyDown={handleKeyDown}
          validate={false}
          errorMessage={passwordError}
          heightProp={'6vmin'}
        />
      </div>
      <div className="mb-4 md:mb-2 lg:mb-3">
        <div className="flex justify-between items-center">
          <Checkbox
            data-testid="rememberme_checkbox"
            checked={rememberMe}
            onChange={handleRememberMeChange}
          >
            <span className='text-[#000000]'>{t('Remember Me')}</span>
          </Checkbox>
          <Link
            data-testid="forgot_password-link"
            href="/auth/forgot-password"
            className="text-blue-500 hover:underline"
          >
            {t('Forgot Password?')}
          </Link>
        </div>
      </div>
      <div className="mb-4 md:mb-2 lg:mb-6">
        <PrimaryButton
          data-testid="login_SignIn"
          theme="primary"
          primaryColor={{
            backgroundColor: '#1D4E89',
            color: '#FFFFFF',
            borderColor: '#1D4E89',
          }}
          disabled={loading}
          height={'7vmin'}
          sizeType='large'
          onClick={handleLoginClick}
          className="w-full font-medium"
        >
          {loading ? t('Signing In...') : t('Sign In')}
        </PrimaryButton>
      </div>
      <div className="mb-2 text-black 2xl:text-base">{t("Don't have an existing account?")}</div>
      <div className="mb-4 md:mb-2 lg:mb-3">
        <PrimaryButton
          data-testid="login_SignUp"
          theme="secondary"
          secondaryColor={{
            backgroundColor: 'transparent',
            color: '#0298A0',
            borderColor: '#0298A0',
          }}
          disabled={false}
          height={'7vmin'}
          onClick={handleRegisterClick}
          className="w-full font-medium"
          sizeType='large'
        >
          {t('Sign Up')}
        </PrimaryButton>
      </div>
      <CustomModal
        data-testid="login_modal"
        isVisible={isModalVisible}
        onCancel={closeModal}
        onOk={activeSession ? (e: React.MouseEvent<HTMLButtonElement>) => handleLogout(e) : closeModal}
        title={description?.heading}
        description={description?.message}
        iconType={description?.type}
        primaryButtonText={ButtonTitle}
        secondaryButtonText=""
      />
    </div>
  );
};

export default LoginBox;
